<template>
  <div id="app" class="app" v-if="locale">
    <app-header />

    <portal :to="subHeaderPortalTarget">
      <app-sub-header @back="onBack" :hide-logo="hideSubHeaderLogo" />
    </portal>
    <portal-target v-if="!$breakpoint.lgAndUp" name="subHeaderFull" slim />

    <div class="app__body" :class="bodyClass">
      <transition :name="transitionName">
        <router-view />
      </transition>
    </div>

    <app-footer :menu-items="footerMenu" />
    <app-update-available-modal
      v-model="showUpdateAvailableModal"
      @reload="onReload"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppHeader from "@/components/layout/Header";
import AppFooter from "@/components/layout/Footer";
import AppSubHeader from "@/components/layout/SubHeader";
import { Portal, PortalTarget } from "portal-vue";
import AppUpdateAvailableModal from "@/components/layout/UpdateAvailableModal";
import Visibility from "visibilityjs";

export default {
  name: "App",
  components: {
    AppUpdateAvailableModal,
    AppSubHeader,
    AppHeader,
    AppFooter,
    Portal,
    PortalTarget,
  },
  created() {
    this.initialize();
    window.addEventListener("updateAvailable", this.onUpdateAvailable);
    Visibility.change((e, state) => {
      if (state === "visible") {
        this.refreshOnVisible();
      }
    });
  },
  beforeDestroy() {
    window.removeEventListener("updateAvailable", this.onUpdateAvailable);
  },
  methods: {
    ...mapActions(["initialize", "refreshOnVisible"]),
    ...mapActions("friendLists", ["acceptFriendListInvite"]),
    onUpdateAvailable(e) {
      this.showUpdateAvailableModal = true;
      this.$serviceWorker = e.detail;
    },
    async onReload() {
      try {
        this.showUpdateAvailableModal = false;
        if (!this.$serviceWorker) {
          return;
        }

        await this.$serviceWorker.postMessage({ action: "skipWaiting" });
      } catch (e) {
        console.error(e);
      } finally {
        window.location.reload();
      }
    },
    onBack(location) {
      this.$router.push(location);
    },
  },
  computed: {
    ...mapGetters(["locale"]),
    ...mapGetters("menus", ["footerMenu"]),
    ...mapGetters("customers", ["customer"]),
    hideSubHeaderLogo() {
      return (
        this.$route.name === "home" && Number(this.$route.params.slide) === 0
      );
    },
    subHeaderPortalTarget() {
      if (this.$breakpoint.lgAndUp) {
        return "subHeaderMiddle";
      }

      return "subHeaderFull";
    },
    bodyClass() {
      const isDark = this.$route.matched.some(record => !!record.meta.dark);
      return {
        "app__body--dark": isDark,
      };
    },
  },
  watch: {
    $route(to, from) {
      const toDepth = to.path.split("/").length;
      const fromDepth = from.path.split("/").length;

      this.transitionName = toDepth < fromDepth ? "slide-right" : "slide-left";
    },
  },
  data() {
    return {
      transitionName: "slide-left",
      showUpdateAvailableModal: false,
    };
  },
};
</script>

<style lang="scss" scoped>
.app {
  &__body {
    &--dark {
      min-height: calc(100vh - 4.125rem);
      position: relative;
      margin-top: -3.125rem;
      background-color: $black;
      background-position: center;
      background-size: cover;
      background-image: url(~@/assets/images/bg-city.png);
      padding: 3.125rem 1.5rem 3.5rem 1.5rem;
    }
  }
}
</style>

<style lang="scss">
@import "./styles/general/index";
</style>
